import axios from 'axios';
import log from '../utils/logger';

class GenericDataProvider {
  constructor(apiClient) {
    this.apiClient = axios;
  }

  async getList(
    objectName,
    options = {
      page: 1,
      pageSize: 10000,
      filter: {},
      queryParams: {},
    },
  ) {
    if (!objectName) {
      throw new Error('List attempt: No object name provided');
    }
    const {
      page = 1, pageSize = 10000, filter = {}, sort,
    } = options;

    // generate filter string in format `filter=key:value`
    // multiple values for a single field are joined with `|`
    const filterString = filter
      ? Object.keys(filter)
        .map((key) => {
          const value = Array.isArray(filter[key]) ? filter[key].join('|') : filter[key];
          // Frontend validation: skip empty/null values or strings ending with colon
          // Note: explicitly allow boolean false (value === false is valid)
          if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.endsWith(':'))) {
            log.warn(`Skipping invalid filter value for key '${key}': empty or trailing colon`, { key, value });
            return null;
          }
          return `${key}:${value}`;
        })
        .filter(Boolean) // Remove nulls from skipped filters
        .join(',')
      : '';
    const queryParamsString = options.queryParams
      ? Object.keys(options.queryParams)
        .map((key) => `&${key}=${options.queryParams[key]}`)
        .join('&')
      : '';
    const sortString = sort ? `&sort=${sort}` : '';
    try {
      const url = `/${objectName}`;
      const queryString = `?page=${page}&pageSize=${pageSize}&filter=${filterString}${queryParamsString}${sortString}`;
      const response = await this.apiClient.get(url + queryString);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${this.objectName}): ${error}`);
      throw error;
    }
  }

  async getOne(objectName, id) {
    if (!id || !objectName) {
      throw new Error('Get item attempt: No object name or id provided');
    }
    try {
      const response = await this.apiClient.get(`/${objectName}/${id}`);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${objectName}): ${error}`);
      throw error;
    }
  }

  async triggerAction(objectName, id, actionName) {
    if (!id || !objectName || !actionName) {
      throw new Error('Trigger action attempt: No object name, id or action name provided');
    }
    try {
      const response = await this.apiClient.post(`/${objectName}/${id}/${actionName}`);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${objectName}): ${error}`);
      throw error;
    }
  }

  async customQuery(method = 'POST', url, data = null) {
    if (!url) {
      throw new Error('customQuery: No url provided');
    }
    try {
      const response = await this.apiClient[method.toLowerCase()](url, data);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (customQuery): ${error}`);
      throw error;
    }
  }

  async create(objectName, data, options = { useFormData: false, onProgressCallback: null, timeout: null }) {
    if (!data || !objectName) {
      throw new Error('Create attempt: No object name or data provided');
    }

    let payload = data;

    if (options?.useFormData) {
      if (!(data instanceof FormData)) {
        payload = new FormData();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            payload.append(key, data[key]);
          }
        }
      }
    }

    const config = {
      headers: {
        'Content-Type': options?.useFormData ? 'multipart/form-data' : 'application/json',
      },
      ...(options?.abortSignal ? { signal: options.abortSignal } : {}),
      ...(options?.onProgressCallback ? { onUploadProgress: options.onProgressCallback } : {}),
      ...(options?.timeout !== null ? { timeout: options.timeout } : {}),
    };

    try {
      const response = await this.apiClient.post(`/${objectName}`, payload, config);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${objectName}): ${error}`);
      throw error;
    }
  }

  async update(objectName, id, data) {
    if (!id || !data || !objectName) {
      throw new Error('Update attempt: No object name, id or data provided');
    }
    try {
      const response = await this.apiClient.put(`/${objectName}/${id}`, data);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${objectName}): ${error}`);
      throw error;
    }
  }

  async delete(objectName, id) {
    if (!id) {
      throw new Error('Delete attempt: No ID provided');
    }
    try {
      const response = await this.apiClient.delete(`/${objectName}/${id}`);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${objectName}): ${error}`);
      throw error;
    }
  }

  // Linking of to objects, like linking a user to a course

  async getLinkedItems(
    parentObjectName,
    parentId,
    childObjectName,
    options = {
      page: 1,
      pageSize: 10000,
      filter: {},
    },
  ) {
    if (!parentObjectName || !parentId || !childObjectName) {
      throw new Error('List attempt: No parent object name, parent id or child object name provided');
    }
    const {
      page = 1, pageSize = 10000, filter = {}, sort,
    } = options;

    // generate filter string in format `filter=key:value`
    // multiple values for a single field are joined with `|`
    const filterString = filter
      ? Object.keys(filter)
        .map((key) => {
          const value = Array.isArray(filter[key]) ? filter[key].join('|') : filter[key];
          return `${key}:${value}`;
        })
        .join(',')
      : '';
    const sortString = sort ? `&sort=${sort}` : '';
    try {
      const url = `/${parentObjectName}/${parentId}/${childObjectName}`;
      const queryString = `?page=${page}&pageSize=${pageSize}&filter=${filterString}${sortString}`;
      const response = await this.apiClient.get(url + queryString);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${parentObjectName}/${childObjectName}): ${error}`);
      throw error;
    }
  }

  async linkItems(parentObjectName, parentId, childObjectName, childId) {
    if (!parentId || !childId || !parentObjectName || !childObjectName) {
      throw new Error('Link attempt: No parent or child object name or ID provided');
    }
    try {
      const response = await this.apiClient.post(`/${parentObjectName}/${parentId}/${childObjectName}/${childId}`);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${parentObjectName}): ${error}`);
      throw error;
    }
  }

  async updateLink(parentObjectName, parentId, childObjectName, childId, data) {
    if (!parentId || !childId || !parentObjectName || !childObjectName || !childId || !data) {
      throw new Error('Link update attempt: Missing information');
    }
    try {
      const response = await this.apiClient.put(`/${parentObjectName}/${parentId}/${childObjectName}/${childId}`, data);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${parentObjectName}): ${error}`);
      throw error;
    }
  }

  async unlinkItems(parentObjectName, parentId, childObjectName, childId) {
    if (!parentId || !childId || !parentObjectName || !childObjectName) {
      throw new Error('Unlink attempt: No parent or child object name or ID provided');
    }
    try {
      const response = await this.apiClient.delete(`/${parentObjectName}/${parentId}/${childObjectName}/${childId}`);
      return response.data || {};
    } catch (error) {
      log.info(`Api Error (${parentObjectName}): ${error}`);
      throw error;
    }
  }
}

export default GenericDataProvider;
